import { useCallback, useMemo } from 'react';

import { Box, Paper, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';

import Tabs from '@/components/Tabs';
import { FWDCURVE_CATALOG_PATH, SPOT_CATALOG_PATH } from '@/constants/constants';
import { PageInformationContext } from '@/context/PageInformationContext';
import useDocumentTitleRename from '@/hooks/useDocumentTitleRename';
import getAmountLabel from '@/utils/getAmountLabel';

import CatalogSkeleton from './CatalogSkeleton';
import CatalogTable from './CatalogTable';
import { FilterProvider } from './contexts/filter/FilterContext';
import { useFilter } from './contexts/filter/useFilter';
import useCatalog from './useCatalog';

const catalogTabs = [
  {
    name: 'Spot',
    label: 'Spot'
  },
  {
    name: 'Forward curves',
    label: 'Forward Curves'
  }
];

const pageInformation = {
  mainPageTitle: 'CatalogTable'
};

const CatalogBase = () => {
  const location = useLocation();
  const pathname = location.pathname.toLowerCase();
  const isForwardCurves =
    pathname.includes(FWDCURVE_CATALOG_PATH) || pathname.includes('forward-curves');

  const {
    status,
    filterOpen,
    setFilterOpen,
    table,
    activeTab,

    expandedData,
    setExpandedData,
    showDiscontinued,
    setShowDiscontinued
  } = useCatalog(isForwardCurves);

  const catalogLabel = useMemo(() => {
    if (isForwardCurves) return 'curve';
    return expandedData ? 'item' : 'code';
  }, [expandedData, isForwardCurves]);

  useDocumentTitleRename('GX Go | Catalog');
  const { clearAllFilters } = useFilter();
  const navigate = useNavigate();

  const onTabChange = useCallback(() => {
    clearAllFilters();
    navigate(`/catalog/${isForwardCurves ? SPOT_CATALOG_PATH : FWDCURVE_CATALOG_PATH}`);
  }, [clearAllFilters, isForwardCurves, navigate]);

  if (status === 'loading') {
    return <CatalogSkeleton />;
  }

  return (
    <>
      <Box sx={{ display: 'flex', width: '100%', my: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'flex-end', width: 390 }}>
          <Typography
            noWrap
            sx={{ mr: 3 }}
            variant='h2'
          >
            Catalog
          </Typography>
          <Typography
            noWrap
            variant='h6'
          >
            {getAmountLabel(table.getFilteredRowModel().rows.length, catalogLabel)}
          </Typography>
        </Box>
        <PageInformationContext.Provider value={pageInformation}>
          <Tabs
            activeTab={activeTab}
            minTabWidth={300}
            tabs={catalogTabs}
            onChange={onTabChange}
          />
        </PageInformationContext.Provider>
      </Box>
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0
        }}
      >
        <CatalogTable
          expandedData={expandedData}
          filterOpen={filterOpen}
          isForwardCurves={isForwardCurves}
          setExpandedData={setExpandedData}
          setFilterOpen={setFilterOpen}
          setShowDiscontinued={setShowDiscontinued}
          showDiscontinued={showDiscontinued}
          table={table}
        />
      </Paper>
    </>
  );
};

const Catalog = () => (
  <FilterProvider>
    <CatalogBase />
  </FilterProvider>
);
export default Catalog;
